import React, { useEffect, useState } from "react";
import { api, API, currentToken } from "@/lib/api";
import { StatusBadge } from "@/components/shared/Common";
import { toast } from "sonner";
import { Check, X, PauseCircle, Search, FileText, Eye } from "lucide-react";

export default function AdminDoctors() {
  const [docs, setDocs] = useState([]);
  const [filter, setFilter] = useState("");
  const [note, setNote] = useState({});
  const [viewing, setViewing] = useState(null); // { doctor, documents }

  const refresh = () => api.get("/admin/doctors").then(r => setDocs(r.data));
  useEffect(() => { refresh(); }, []);

  const act = async (id, status) => {
    try {
      await api.put(`/admin/doctors/${id}/verify`, { status, note: note[id] || "" });
      toast.success(`Marked ${status}`);
      refresh();
    } catch { toast.error("Failed"); }
  };

  const viewDocs = async (d) => {
    try {
      const { data } = await api.get(`/admin/doctors/${d.user_id}/documents`);
      setViewing({ doctor: d, documents: data });
    } catch { toast.error("Could not load documents"); }
  };

  const openFile = async (id) => {
    try {
      const res = await fetch(`${API}/documents/${id}/download`, { headers: { Authorization: `Bearer ${currentToken()}` } });
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch { toast.error("Could not open"); }
  };

  const filtered = docs.filter(d => filter ? d.verification_status === filter : true);

  return (
    <div className="space-y-6">
      <div>
        <div className="cm-eyebrow mb-2">Doctors &amp; Verification</div>
        <h1 className="cm-h2">Manage your clinical network</h1>
      </div>

      <div className="flex flex-wrap gap-2">
        {["", "pending", "under_review", "verified", "rejected", "suspended"].map(s => (
          <button key={s||"all"} onClick={()=>setFilter(s)} className={`rounded-full px-4 py-1.5 text-xs font-medium border ${filter===s ? "bg-[#1a2b4a] text-white border-[#1a2b4a]" : "bg-white border-[#e2e8f0] text-[#475569] hover:border-[#4a90d9]"}`} data-testid={`filter-${s||"all"}`}>{s || "All"}</button>
        ))}
      </div>

      <div className="cm-card divide-y divide-[#e2e8f0]">
        {filtered.map(d => (
          <div key={d.id} className="py-4 first:pt-0 last:pb-0 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3" data-testid={`admin-doctor-${d.user_id}`}>
            <div>
              <div className="font-semibold text-[#1a2b4a]">Dr. {d.first_name} {d.last_name}</div>
              <div className="text-xs text-[#475569] capitalize">{(d.specialty||"").replace("_"," ")} · {d.registration_number || "no reg"} · {d.years_experience || 0} yrs</div>
              <div className="text-xs text-[#94a3b8]">{d.email}</div>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
              <StatusBadge status={d.verification_status}/>
              <button className="cm-btn-outline text-xs py-1.5 px-2.5" onClick={()=>viewDocs(d)} data-testid={`docs-${d.user_id}`}><FileText size={14}/>Docs</button>
              <input placeholder="Reviewer note" className="cm-input py-1.5 text-xs w-56" value={note[d.user_id]||""} onChange={e=>setNote({...note, [d.user_id]:e.target.value})} data-testid={`note-${d.user_id}`}/>
              <div className="flex gap-1">
                <button className="cm-btn-outline text-xs py-1.5 px-2.5" onClick={()=>act(d.user_id, "verified")} data-testid={`verify-${d.user_id}`}><Check size={14}/></button>
                <button className="cm-btn-outline text-xs py-1.5 px-2.5" onClick={()=>act(d.user_id, "under_review")} data-testid={`review-${d.user_id}`}><Search size={14}/></button>
                <button className="cm-btn-outline text-xs py-1.5 px-2.5" onClick={()=>act(d.user_id, "suspended")} data-testid={`suspend-${d.user_id}`}><PauseCircle size={14}/></button>
                <button className="cm-btn-outline text-xs py-1.5 px-2.5" onClick={()=>act(d.user_id, "rejected")} data-testid={`reject-${d.user_id}`}><X size={14}/></button>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="py-3 text-sm text-[#475569]">No doctors match this filter.</div>}
      </div>

      {viewing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={()=>setViewing(null)} data-testid="docs-dialog">
          <div className="bg-white rounded-xl border border-[#e2e8f0] max-w-2xl w-full p-6 shadow-xl" onClick={e=>e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="cm-eyebrow mb-1">Verification documents</div>
                <h3 className="font-semibold text-[#1a2b4a]">Dr. {viewing.doctor.first_name} {viewing.doctor.last_name}</h3>
              </div>
              <button onClick={()=>setViewing(null)} className="p-1 hover:bg-[#f5f7fa] rounded" data-testid="docs-close"><X size={18}/></button>
            </div>
            <div className="divide-y divide-[#e2e8f0] max-h-96 overflow-y-auto">
              {viewing.documents.length === 0 && <div className="text-sm text-[#475569] py-2">No documents uploaded yet.</div>}
              {viewing.documents.map(doc => (
                <div key={doc.id} className="py-3 flex items-center gap-3">
                  <FileText size={20} className="text-[#4a90d9]"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#1a2b4a] truncate">{doc.filename}</div>
                    <div className="text-xs text-[#475569] capitalize">{(doc.kind||"").replace("doctor_verification_","").replace("_"," ")} · {(doc.size/1024).toFixed(0)} KB</div>
                    {doc.note && <div className="text-xs text-[#94a3b8] italic">"{doc.note}"</div>}
                  </div>
                  <button className="cm-btn-primary text-xs py-1.5 px-3" onClick={()=>openFile(doc.id)} data-testid={`docs-open-${doc.id}`}><Eye size={14}/>Open</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
